import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight } from '@phosphor-icons/react';
import useSeo from '../../hooks/useSeo';

const SITE = 'https://contratti.vitadahost.it';

/**
 * Shared GEO-friendly guide layout.
 *  • Proper semantic heading hierarchy (h1 → h2 → h3)
 *  • Article + FAQPage + (optional) HowTo JSON-LD
 *  • Breadcrumb schema
 *  • Auto canonical + OG / Twitter
 *
 * Props:
 *   slug, title, description, datePublished, dateModified,
 *   readingTime, breadcrumbLabel,
 *   children, faqs:[{q,a}], howTo:{name, steps:[{name, text}]}
 */
export default function GuideLayout({
  slug,
  title,
  description,
  datePublished = '2026-02-22',
  dateModified = '2026-02-22',
  readingTime = 10,
  breadcrumbLabel,
  faqs = [],
  howTo,
  children,
  image,
}) {
  const url = `${SITE}/guide/${slug}`;
  const imageUrl = image || `${SITE}/og-cover.png`;

  // Build @graph with multiple Schema.org entities
  const graph = [
    {
      '@type': 'Article',
      headline: title,
      description,
      datePublished,
      dateModified,
      author: { '@type': 'Organization', name: 'Locazione Turistica · Vita da Host' },
      publisher: {
        '@type': 'Organization',
        name: 'Locazione Turistica · Vita da Host',
        logo: { '@type': 'ImageObject', url: `${SITE}/icon.png` },
      },
      image: imageUrl,
      mainEntityOfPage: url,
      inLanguage: 'it-IT',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Guide', item: `${SITE}/guide` },
        { '@type': 'ListItem', position: 3, name: breadcrumbLabel || title, item: url },
      ],
    },
  ];

  if (faqs.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faqs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
  }

  if (howTo && howTo.steps?.length) {
    graph.push({
      '@type': 'HowTo',
      name: howTo.name || title,
      step: howTo.steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }

  useSeo({
    title: `${title} | Locazione Turistica`,
    description,
    canonical: url,
    image: imageUrl,
    type: 'article',
    jsonLd: { '@context': 'https://schema.org', '@graph': graph },
  });

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500 mb-4">
            <Link to="/" className="hover:text-zinc-900">Home</Link>
            <span className="text-zinc-300">/</span>
            <Link to="/guide" className="hover:text-zinc-900">Guide</Link>
            <span className="text-zinc-300">/</span>
            <span className="text-zinc-900">{breadcrumbLabel || title}</span>
          </nav>

          <h1 className="text-3xl sm:text-5xl font-black text-zinc-900 tracking-tight leading-tight">
            {title}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">
            {description}
          </p>
          <div className="mt-5 flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">
            <span className="flex items-center gap-1.5">
              <Clock size={12} weight="bold" /> {readingTime} min di lettura
            </span>
            <span className="text-zinc-300">·</span>
            <span>Aggiornato {dateModified}</span>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white border border-zinc-200 p-6 sm:p-10">
          <div className="guide-prose">{children}</div>

          {/* FAQ Section (rendered visibly because LLMs love both schema + visible text) */}
          {faqs.length > 0 && (
            <section className="mt-10 sm:mt-14 pt-8 border-t border-zinc-200">
              <h2 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight mb-4">Domande frequenti</h2>
              <div className="space-y-3">
                {faqs.map((f, i) => (
                  <details key={i} className="border border-zinc-200 p-4">
                    <summary className="cursor-pointer text-sm font-bold text-zinc-900 list-none flex items-start gap-2">
                      <span className="text-emerald-600">›</span>
                      <span>{f.q}</span>
                    </summary>
                    <p className="mt-2 ml-4 text-sm text-zinc-700 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* CTA */}
        <div className="mt-8 sm:mt-12 bg-zinc-900 text-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-400 mb-1.5">
              Stop leggere · inizia a fare.
            </p>
            <p className="text-base sm:text-lg font-black tracking-tight">
              Locazione Turistica fa per te il 90% di quello che hai appena letto. Provala 7 giorni gratis.
            </p>
          </div>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-colors flex-shrink-0"
            data-testid={`guide-cta-${slug}`}
          >
            Inizia gratis <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        <Link to="/guide" className="mt-8 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500 hover:text-zinc-900">
          <ArrowLeft size={11} weight="bold" /> Tutte le guide
        </Link>
      </article>

      {/* Inline styles for the prose markup */}
      <style>{`
        .guide-prose { color: #3f3f46; font-size: 15px; line-height: 1.75; }
        .guide-prose h2 { font-size: 1.4rem; font-weight: 900; color: #18181b; letter-spacing: -0.02em; margin: 2rem 0 0.8rem; }
        .guide-prose h3 { font-size: 1.05rem; font-weight: 900; color: #18181b; margin: 1.5rem 0 0.6rem; }
        .guide-prose h2:first-child, .guide-prose h3:first-child { margin-top: 0; }
        .guide-prose p { margin: 0 0 1rem; }
        .guide-prose strong { color: #18181b; font-weight: 700; }
        .guide-prose ul, .guide-prose ol { margin: 0 0 1.1rem 1.4rem; }
        .guide-prose ul { list-style: disc; }
        .guide-prose ol { list-style: decimal; }
        .guide-prose li { margin-bottom: 0.4rem; }
        .guide-prose a { color: #047857; text-decoration: underline; text-underline-offset: 2px; }
        .guide-prose a:hover { color: #064e3b; }
        .guide-prose blockquote { border-left: 3px solid #0d9488; padding: 0.3rem 0 0.3rem 1rem; margin: 1.2rem 0; color: #52525b; font-style: italic; background: #f0fdfa; }
        .guide-prose code { background: #fafafa; border: 1px solid #e4e4e7; padding: 1px 5px; font-size: 0.875em; font-family: ui-monospace, monospace; color: #18181b; }
        .guide-prose table { width: 100%; border-collapse: collapse; margin: 1.2rem 0; font-size: 0.92em; }
        .guide-prose th, .guide-prose td { border: 1px solid #e4e4e7; padding: 0.5rem 0.7rem; text-align: left; vertical-align: top; }
        .guide-prose th { background: #fafafa; font-weight: 700; color: #18181b; font-size: 0.82em; text-transform: uppercase; letter-spacing: 0.05em; }
        .guide-prose .callout { background: #fef3c7; border-left: 3px solid #f59e0b; padding: 0.8rem 1rem; margin: 1.2rem 0; font-size: 0.92em; color: #78350f; }
        .guide-prose .callout-info { background: #f0fdfa; border-left-color: #0d9488; color: #134e4a; }
      `}</style>
    </div>
  );
}
